import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';
import { calculatePrice, getCityFromPostalCode } from '@/lib/pricing';
import { AlertCircle } from 'lucide-react';

function cmToInches(cm: number): number {
  return Math.round(cm / 2.54);
}

function inchesToCm(inches: number): number {
  return Math.round(inches * 2.54);
}

interface QuoteFormData {
  postalCode: string;
  drivewaySize: 'small' | 'medium' | 'large';
  snowDepthCm: number;
  timeWindow: 'asap' | 'same_day' | 'flexible';
}

interface QuoteResult {
  customerPrice: number;
  breakdown: {
    base: number;
    depthFactor: number;
    timeFactor: number;
    cityMultiplier: number;
    addonsTotal: number;
  };
  estimatedDuration: number;
  cityName?: string;
}

export function QuoteWidget() {
  const [unit, setUnit] = useState<'cm' | 'in'>('in');
  const [formData, setFormData] = useState<QuoteFormData>({
    postalCode: '',
    drivewaySize: 'medium',
    snowDepthCm: 10,
    timeWindow: 'asap',
  });

  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [error, setError] = useState<string>('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [detectedCity, setDetectedCity] = useState<string>('');

  const displayDepth = unit === 'in' ? cmToInches(formData.snowDepthCm) : formData.snowDepthCm;
  const minDepth = unit === 'in' ? cmToInches(5) : 5;
  const maxDepth = unit === 'in' ? cmToInches(40) : 40;

  const handleDepthChange = (value: number) => {
    const depthCm = unit === 'in' ? inchesToCm(value) : value;
    setFormData({ ...formData, snowDepthCm: depthCm });
  };

  // Auto-detect city from postal code
  useEffect(() => {
    if (formData.postalCode.length >= 3) {
      const citySlug = getCityFromPostalCode(formData.postalCode);
      if (citySlug) {
        setDetectedCity(citySlug);
        setError('');
      } else {
        setDetectedCity('');
      }
    } else {
      setDetectedCity('');
    }
  }, [formData.postalCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsCalculating(true);
    setError('');
    setQuote(null);

    try {
      // Get city from postal code
      const citySlug = getCityFromPostalCode(formData.postalCode);
      
      if (!citySlug) {
        setError('Unable to find your city. Please check your postal code.');
        setIsCalculating(false);
        return;
      }

      // Calculate price
      const result = calculatePrice({
        citySlug,
        drivewaySize: formData.drivewaySize,
        snowDepthCm: formData.snowDepthCm,
        timeWindow: formData.timeWindow
      });

      setQuote({
        ...result,
        cityName: citySlug
      });

      trackEvent('quote_calculated', { 
        postalCode: formData.postalCode.substring(0, 3), 
        size: formData.drivewaySize,
        price: result.customerPrice 
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to calculate quote. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="bg-snow-100 rounded-xl p-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Postal Code {detectedCity && <span className="text-accent-500">({detectedCity})</span>}
          </label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value.toUpperCase() })}
            placeholder="K1A 1W2"
            maxLength={7}
            className="w-full rounded-lg border border-neutral-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 uppercase"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Driveway Size</label>
          <select
            value={formData.drivewaySize}
            onChange={(e) => setFormData({ ...formData, drivewaySize: e.target.value as any })}
            className="w-full rounded-lg border border-neutral-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="small">Small (1 car)</option>
            <option value="medium">Medium (2 cars)</option>
            <option value="large">Large (3+ cars)</option>
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">
              Snow Depth: {displayDepth}{unit === 'in' ? '"' : 'cm'}
              {unit === 'in' && ` (${formData.snowDepthCm}cm)`}
              {unit === 'cm' && ` (${cmToInches(formData.snowDepthCm)}")`}
            </label>
            <button
              type="button"
              onClick={() => setUnit(unit === 'cm' ? 'in' : 'cm')}
              className="text-xs px-2 py-1 bg-neutral-200 hover:bg-neutral-300 rounded font-medium"
            >
              {unit === 'cm' ? 'Switch to Inches' : 'Switch to CM'}
            </button>
          </div>
          <input
            type="range"
            min={minDepth}
            max={maxDepth}
            value={displayDepth}
            onChange={(e) => handleDepthChange(parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-neutral-600 mt-1">
            <span>{minDepth}{unit === 'in' ? '"' : 'cm'}</span>
            <span>{maxDepth}{unit === 'in' ? '"' : 'cm'}</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">When do you need it?</label>
          <select
            value={formData.timeWindow}
            onChange={(e) => setFormData({ ...formData, timeWindow: e.target.value as any })}
            className="w-full rounded-lg border border-neutral-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="asap">ASAP (within 2-4 hours) +30%</option>
            <option value="same_day">Today (flexible) +10%</option>
            <option value="flexible">Flexible (up to 24hr)</option>
          </select>
        </div>

        <Button 
          type="submit" 
          variant="primary" 
          className="w-full" 
          disabled={isCalculating || formData.postalCode.length < 6}
        >
          {isCalculating ? 'Calculating...' : 'Get Instant Price'}
        </Button>
      </form>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {quote && (
        <div className="mt-6 p-4 bg-white rounded-lg border-2 border-brand-500 animate-fade-up">
          <div className="text-3xl font-black text-brand-500 mb-2">
            ${quote.customerPrice}
          </div>
          <div className="text-sm text-neutral-700 space-y-1">
            <div className="flex justify-between">
              <span>Base rate ({formData.drivewaySize}):</span>
              <span>${quote.breakdown.base}</span>
            </div>
            <div className="flex justify-between">
              <span>Snow depth factor:</span>
              <span>{quote.breakdown.depthFactor.toFixed(2)}x</span>
            </div>
            <div className="flex justify-between">
              <span>Time window:</span>
              <span>{quote.breakdown.timeFactor.toFixed(2)}x</span>
            </div>
            <div className="flex justify-between">
              <span>City adjustment:</span>
              <span>{quote.breakdown.cityMultiplier.toFixed(2)}x</span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500 pt-2 border-t">
              <span>Est. duration:</span>
              <span>{quote.estimatedDuration} mins</span>
            </div>
          </div>
          <Button 
            variant="primary" 
            size="sm" 
            className="w-full mt-4"
            onClick={() => {
              trackEvent('quote_book_clicked', { price: quote.customerPrice });
              document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Book this clear
          </Button>
        </div>
      )}
    </div>
  );
}
